// 원서 문제지 쪽을 화면 크기만큼 자른다(PaperCropPage). 서버가 출제 때 적어 둔 '글이 있는 띠'
// (page.layout, api/assignment/paper_layout.py)를 이어 붙이다가 화면 높이를 넘기 전에 띠 사이 빈 줄에서 자른다.
// 한 문항(번호부터 마지막 칸까지)은 가운데서 자르지 않는다. 좌표는 모두 쪽 비율(0~1)이다.

const DEFAULT_ASPECT = 1.415;
// 조각을 늘려 칸을 다 보이게 할 때의 여유
const QUESTION_TOP_MARGIN = 0.012;
const QUESTION_BOTTOM_MARGIN = 0.008;
// 문항 번호(가운데 좌표) 위로 이만큼까지가 그 문항 글줄이다. 자르면 안 되는 자리를 잴 때 쓴다.
const NUMBER_HALF_LINE = 0.004;
const NUMBER_HALF_WIDTH = 0.02;
// 문항이 하나도 없는 짧은 조각(활동 머리 · 지시문)은 혼자 두지 않고 다음 문항과 함께 낸다
const LONELY_HEADING_SHARE = 0.5;
const CROP_PADDING = 0.014;
// 이만큼 넓은 빈 틈은 단락 · 활동이 바뀌는 자리다. 같은 높이 안이면 이런 자리에서 자르는 것을 먼저 고른다.
const SECTION_GAP = 0.02;
// 아주 낮은 화면에서도 조각이 띠 한두 줄로 쪼개지지 않게
const MINIMUM_CROP_HEIGHT = 0.18;
// 이보다 좁은 조각(두 단 쪽의 한 단)은 넓은 가로 화면에서 둘을 나란히 놓는다
const NARROW_CROP_WIDTH = 0.6;
export const MAXIMUM_TILE_WIDTH = 960;
const MAXIMUM_PAGE_WIDTH = 1300;
const SIDE_PADDING = 0.025;
const WIDTH_BREAK_RATIO = 1.4;
export const TILE_GAP = 16;

const FALLBACK_COLUMN = { left: 0, right: 1, runs: [[0, 1]] };

const layoutOf = (page) => {
  const layout = page.layout || {};
  const columnList = (layout.columns || []).filter((column) => (column.runs || []).length > 0);
  return {
    aspect: layout.aspect || DEFAULT_ASPECT,
    columnList: columnList.length ? columnList : [FALLBACK_COLUMN],
  };
};

// 칸 하나가 차지하는 자리(번호 · 쓰는 칸 · 보기 · 선 잇기 점)
const boundsOfField = (field) => {
  const boxList = [field.box, ...(field.pieces || []), ...(field.options || []).map((option) => option.box)].filter(
    (box) => box && typeof box.y === "number"
  );
  const pointList = [field.number, ...(field.left || []), ...(field.right || [])].filter(
    (point) => point && typeof point.y === "number"
  );
  const topList = [...boxList.map((box) => box.y), ...pointList.map((point) => point.y)];
  const bottomList = [...boxList.map((box) => box.y + (box.h || 0)), ...pointList.map((point) => point.y)];
  if (!topList.length) return null;
  const centerX = field.number?.x ?? (boxList[0] ? boxList[0].x + (boxList[0].w || 0) / 2 : pointList[0].x);
  // 번호 가운데에서 조금 왼쪽까지(번호 글자 폭), 칸 · 점의 오른쪽 끝까지
  const leftList = [...boxList.map((box) => box.x), ...pointList.map((point) => point.x)];
  const rightList = [...boxList.map((box) => box.x + (box.w || 0)), ...pointList.map((point) => point.x)];
  if (field.number) leftList.push(field.number.x - NUMBER_HALF_WIDTH);
  return {
    top: Math.min(...topList),
    bottom: Math.max(...bottomList),
    left: Math.min(...leftList),
    right: Math.max(...rightList),
    centerX,
  };
};

// 빈칸이 여럿인 문항(c0.q73.0, c0.q73.1)은 한 문항으로 묶는다
const questionKeyOf = (fieldId) => String(fieldId).replace(/\.\d+$/, "");

const columnIndexOf = (columnList, x) => {
  const inside = columnList.findIndex((column) => x >= column.left && x <= column.right);
  if (inside >= 0) return inside;
  let best = 0;
  columnList.forEach((column, index) => {
    const distance = Math.min(Math.abs(x - column.left), Math.abs(x - column.right));
    const bestColumn = columnList[best];
    if (distance < Math.min(Math.abs(x - bestColumn.left), Math.abs(x - bestColumn.right))) best = index;
  });
  return best;
};

// 한 단의 띠를 화면 높이 안으로 묶는다. 띠: [위, 아래, 왼쪽, 오른쪽]. 조각: {top, bottom, left, right}
// 화면에 들어갈 높이는 조각 폭에 따라 다르다(좁게 자를수록 크게 그려 덜 들어간다). heightLimitOf(폭) 으로 잰다.
const packColumn = (runList, spanList, heightLimitOf) => {
  const count = runList.length;
  const gapAfter = runList.map((run, index) => (index + 1 < count ? runList[index + 1][0] - run[1] : 1));
  const canCutAfter = runList.map((run, index) => {
    if (index + 1 >= count) return true;
    const middle = (run[1] + runList[index + 1][0]) / 2;
    return !spanList.some((span) => span.cutTop < middle && span.cutBottom > middle);
  });
  const hasQuestionBetween = (topY, bottomY) =>
    spanList.some((span) => span.cutTop >= topY - NUMBER_HALF_LINE && span.cutTop <= bottomY);
  const hasQuestionAfter = (y) => spanList.some((span) => span.cutTop > y);
  const widthOf = (startIndex, endIndex) => {
    const slice = runList.slice(startIndex, endIndex + 1);
    return Math.max(...slice.map((run) => run[3])) - Math.min(...slice.map((run) => run[2])) + SIDE_PADDING * 2;
  };

  const cropList = [];
  let start = 0;
  while (start < count) {
    let furthest = -1;
    let furthestSection = -1;
    let sectionLimit = 0;
    for (let index = start; index < count; index += 1) {
      const height = runList[index][1] - runList[start][0] + CROP_PADDING * 2;
      const limit = heightLimitOf(widthOf(start, index));
      if (height > limit && index > start) {
        if (furthest < 0 && canCutAfter[index]) furthest = index;
        if (furthest >= 0) break;
        continue;
      }
      const isLonelyHeading =
        spanList.length > 0 &&
        height < limit * LONELY_HEADING_SHARE &&
        !hasQuestionBetween(runList[start][0], runList[index][1]) &&
        hasQuestionAfter(runList[index][1]);
      // 다음 띠와 폭이 크게 다르면(넓은 사진 · 답 줄 ↔ 좁은 본문 줄) 여기서 끊는다. 좁은 쪽을 크게 그릴 수 있다.
      const currentWidth = widthOf(start, index);
      const nextWidth = index + 1 < count ? widthOf(index + 1, index + 1) : currentWidth;
      const isWidthBreak =
        Math.max(currentWidth, nextWidth) > Math.min(currentWidth, nextWidth) * WIDTH_BREAK_RATIO &&
        height >= limit * 0.25;
      if (canCutAfter[index] && !isLonelyHeading && isWidthBreak) {
        furthest = index;
        break;
      }
      if (canCutAfter[index] && !isLonelyHeading) {
        furthest = index;
        if (gapAfter[index] >= SECTION_GAP) {
          furthestSection = index;
          sectionLimit = limit;
        }
      }
    }
    // 같은 높이 안이면 활동이 바뀌는 넓은 틈에서 자른다. 그 틈이 너무 앞이면(반도 못 채우면) 그냥 멀리 간다.
    let end = furthest < 0 ? count - 1 : furthest;
    if (furthestSection >= 0 && furthestSection < end) {
      const sectionHeight = runList[furthestSection][1] - runList[start][0];
      if (sectionHeight >= sectionLimit * 0.55) end = furthestSection;
    }
    cropList.push(cropOfRuns(runList, gapAfter, start, end));
    start = end + 1;
  }
  // 끝에 한두 줄만 따로 남으면 앞 조각에 붙인다. 조금 넘쳐도 한 줄짜리 화면보다 낫다.
  if (cropList.length > 1) {
    const last = cropList[cropList.length - 1];
    const previous = cropList[cropList.length - 2];
    const limit = heightLimitOf(Math.max(last.right, previous.right) - Math.min(last.left, previous.left));
    if (last.bottom - last.top < limit * 0.3 && last.bottom - previous.top <= limit * 1.3) {
      previous.bottom = last.bottom;
      previous.left = Math.min(previous.left, last.left);
      previous.right = Math.max(previous.right, last.right);
      cropList.pop();
    }
  }
  return cropList;
};

const cropOfRuns = (runList, gapAfter, start, end) => {
  const count = runList.length;
  const slice = runList.slice(start, end + 1);
  return {
    top: Math.max(0, runList[start][0] - Math.min(CROP_PADDING, start > 0 ? gapAfter[start - 1] / 2 : CROP_PADDING)),
    bottom: Math.min(1, runList[end][1] + Math.min(CROP_PADDING, end + 1 < count ? gapAfter[end] / 2 : CROP_PADDING)),
    left: Math.max(0, Math.min(...slice.map((run) => run[2])) - SIDE_PADDING),
    right: Math.min(1, Math.max(...slice.map((run) => run[3])) + SIDE_PADDING),
  };
};

// 글 띠에 문항 자리(번호부터 쓰는 칸까지)를 더한다. 쓰는 칸 밑줄은 옅어서 그림 분석에 잡히지 않는다.
// 예전 분석(가로 범위 없는 [위, 아래])이면 단 폭을 쓴다.
const mergeRunList = (column, spanList) => {
  const sortedList = [
    ...column.runs.map((run) => [run[0], run[1], run[2] ?? column.left, run[3] ?? column.right]),
    ...spanList.map((span) => [Math.max(0, span.cutTop), Math.min(1, span.cutBottom), span.left, span.right]),
  ].sort((first, second) => first[0] - second[0]);
  const mergedList = [];
  sortedList.forEach((run) => {
    const last = mergedList[mergedList.length - 1];
    if (last && run[0] <= last[1]) {
      last[1] = Math.max(last[1], run[1]);
      last[2] = Math.min(last[2], run[2]);
      last[3] = Math.max(last[3], run[3]);
    } else {
      mergedList.push([...run]);
    }
  });
  return mergedList;
};

const isLandscapeWide = (stageWidth, stageHeight) => stageWidth >= 1000 && stageWidth > stageHeight * 1.25;

// 조각 폭이 cropWidth 일 때 화면에 그릴 폭(px). 좁은 조각을 화면 가득 키우면 PC 에서 글이 너무 커지므로
// 쪽 전체 폭으로 치면 MAXIMUM_PAGE_WIDTH 를 넘지 않게 둔다(폰은 화면 폭이 먼저 걸린다).
export const tileWidthOf = (cropWidth, stageWidth, stageHeight) => {
  const zoomLimit = cropWidth * MAXIMUM_PAGE_WIDTH;
  if (isLandscapeWide(stageWidth, stageHeight) && cropWidth <= NARROW_CROP_WIDTH) {
    return Math.min((stageWidth - TILE_GAP) / 2, MAXIMUM_TILE_WIDTH, zoomLimit);
  }
  return Math.min(stageWidth, MAXIMUM_TILE_WIDTH, zoomLimit);
};

// 쪽 목록 → 조각 목록. 조각: {key, page, left, right, top, bottom, aspect, fieldList}
export const buildCropList = (pageList, stageWidth, stageHeight) => {
  const cropList = [];
  pageList.forEach((page) => {
    const { aspect, columnList } = layoutOf(page);
    const fieldList = page.fields || [];

    // 문항마다 자리. 문항 번호 조금 위부터 마지막 칸까지다.
    const spanByQuestion = {};
    fieldList.forEach((field) => {
      const bounds = boundsOfField(field);
      if (!bounds) return;
      const key = questionKeyOf(field.id);
      const previous = spanByQuestion[key];
      const cutTop = bounds.top - NUMBER_HALF_LINE;
      spanByQuestion[key] = previous
        ? {
            top: Math.min(previous.top, bounds.top - QUESTION_TOP_MARGIN),
            bottom: Math.max(previous.bottom, bounds.bottom + QUESTION_BOTTOM_MARGIN),
            cutTop: Math.min(previous.cutTop, cutTop),
            cutBottom: Math.max(previous.cutBottom, bounds.bottom),
            left: Math.min(previous.left, bounds.left),
            right: Math.max(previous.right, bounds.right),
            centerX: previous.centerX,
          }
        : {
            top: bounds.top - QUESTION_TOP_MARGIN,
            bottom: bounds.bottom + QUESTION_BOTTOM_MARGIN,
            cutTop,
            cutBottom: bounds.bottom,
            left: bounds.left,
            right: bounds.right,
            centerX: bounds.centerX,
          };
    });
    const spanList = Object.values(spanByQuestion);

    const pageCropList = [];
    // 폭이 cropWidth 인 조각이 화면 높이에 들어가는 쪽 높이 비율
    const heightLimitOf = (cropWidth) =>
      Math.max(MINIMUM_CROP_HEIGHT, (stageHeight * cropWidth) / (aspect * tileWidthOf(cropWidth, stageWidth, stageHeight)));
    columnList.forEach((column, columnIndex) => {
      const columnSpanList = spanList.filter(
        (span) => columnList.length === 1 || columnIndexOf(columnList, span.centerX) === columnIndex
      );
      packColumn(mergeRunList(column, columnSpanList), columnSpanList, heightLimitOf).forEach((crop, cropIndex) => {
        pageCropList.push({
          key: `${page.page}-${columnIndex}-${cropIndex}`,
          page: page.page,
          columnIndex,
          left: Math.max(column.left, crop.left),
          right: Math.min(column.right, crop.right),
          top: crop.top,
          bottom: crop.bottom,
          aspect,
          fieldList: [],
        });
      });
    });

    // 칸은 문항 자리의 가운데가 든 조각에 둔다. 띠 밖에 있는 칸은 가장 가까운 조각에 둔다.
    fieldList.forEach((field) => {
      const bounds = boundsOfField(field);
      if (!bounds || !pageCropList.length) return;
      const span = spanByQuestion[questionKeyOf(field.id)] || bounds;
      const middle = (Math.max(0, span.top) + span.bottom) / 2;
      const columnIndex = columnList.length === 1 ? 0 : columnIndexOf(columnList, span.centerX ?? bounds.centerX);
      const candidateList = pageCropList.filter((crop) => crop.columnIndex === columnIndex);
      const pool = candidateList.length ? candidateList : pageCropList;
      const distanceOf = (crop) => (middle < crop.top ? crop.top - middle : middle > crop.bottom ? middle - crop.bottom : 0);
      const target = pool.reduce((best, crop) => (distanceOf(crop) < distanceOf(best) ? crop : best), pool[0]);
      target.fieldList.push(field);
    });
    // 칸이 조각 밖으로 삐져나가면 조각을 늘려 다 보이게 한다
    pageCropList.forEach((crop) => {
      crop.fieldList.forEach((field) => {
        const bounds = boundsOfField(field);
        crop.top = Math.max(0, Math.min(crop.top, bounds.top - QUESTION_TOP_MARGIN));
        crop.bottom = Math.min(1, Math.max(crop.bottom, bounds.bottom + QUESTION_BOTTOM_MARGIN));
        crop.left = Math.max(0, Math.min(crop.left, bounds.left - SIDE_PADDING));
        crop.right = Math.min(1, Math.max(crop.right, bounds.right + SIDE_PADDING));
      });
    });
    cropList.push(...pageCropList);
  });
  return cropList;
};

// 조각 목록 → 화면 목록(조각 순번 배열). 넓은 가로 화면에서는 좁은 조각 둘을 나란히 놓는다.
export const buildScreenList = (cropList, stageWidth, stageHeight) => {
  const isWide = isLandscapeWide(stageWidth, stageHeight);
  const screenList = [];
  for (let index = 0; index < cropList.length; index += 1) {
    const crop = cropList[index];
    const next = cropList[index + 1];
    const isNarrow = (one) => one.right - one.left <= NARROW_CROP_WIDTH;
    if (isWide && next && isNarrow(crop) && isNarrow(next)) {
      screenList.push([index, index + 1]);
      index += 1;
    } else {
      screenList.push([index]);
    }
  }
  return screenList;
};
